/**
 * ToolbarColorPicker
 * Preset color palette popover for the TipTap toolbar.
 * Applies text color via editor.chain().focus().setColor(hex).run().
 */

import { useState, useRef, useEffect } from 'react';
import type { Editor } from '@tiptap/react';
import { Palette } from 'lucide-react';

type ToolbarColorPickerProps = {
  editor: Editor;
};

const PRESET_COLORS = [
  { hex: '#000000', label: 'Black' },
  { hex: '#374151', label: 'Dark Gray' },
  { hex: '#6B7280', label: 'Gray' },
  { hex: '#DC2626', label: 'Red' },
  { hex: '#EA580C', label: 'Orange' },
  { hex: '#CA8A04', label: 'Yellow' },
  { hex: '#16A34A', label: 'Green' },
  { hex: '#026857', label: 'Teal (Brand)' },
  { hex: '#2563EB', label: 'Blue' },
  { hex: '#7C3AED', label: 'Purple' },
  { hex: '#DB2777', label: 'Pink' },
  { hex: '#FFFFFF', label: 'White' },
];

export function ToolbarColorPicker({ editor }: ToolbarColorPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentColor = editor.getAttributes('textStyle').color || '#000000';

  const applyColor = (hex: string) => {
    if (hex === '#000000') {
      editor.chain().focus().unsetColor().run();
    } else {
      editor.chain().focus().setColor(hex).run();
    }
    setIsOpen(false);
  };

  return (
    <div ref={popoverRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Text color"
        className={`p-1.5 rounded transition-colors ${
          isOpen
            ? 'bg-[#026857] text-white'
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
        }`}
      >
        <div className="relative">
          <Palette size={16} />
          <span
            className="absolute -bottom-0.5 left-0 right-0 h-0.5 rounded-full"
            style={{ backgroundColor: currentColor }}
          />
        </div>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 z-20 bg-white rounded-lg shadow-lg border border-gray-200 p-2 w-[164px]">
          <div className="grid grid-cols-6 gap-1">
            {PRESET_COLORS.map((c) => (
              <button
                key={c.hex}
                type="button"
                onClick={() => applyColor(c.hex)}
                title={c.label}
                className={`w-6 h-6 rounded border transition-transform hover:scale-110 ${
                  currentColor === c.hex
                    ? 'ring-2 ring-[#026857] ring-offset-1'
                    : 'border-gray-200'
                } ${c.hex === '#FFFFFF' ? 'border-gray-300' : ''}`}
                style={{ backgroundColor: c.hex }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
